<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'CreateBillingPlan',
  });

  const componentMetadata = {
    kind: 'page',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    modal: {
      type: Boolean,
      required: false,
      default: false,
    },
    modalName: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getPayload = (...args) => {
    const [item] = args;
    const result = _.pick(item, ['day', 'max_date']);

    for (let field of ['contracts', 'rentals', 'companies', 'tenants']) {
      if (_.isArray(item[field])) {
        result[field] = _.map(item[field], 'id');
      }
    }

    return result;
  };

  const formName = ref('CreateBillingPlanOptions');
  const labelAlign = ref('right');
  const item = ref({
    day: $fn.value.today(),
    max_date: null,
    contracts: null,
    companies: null,
    rentals: null,
    tenants: null,
  });
  const ux = ref([]);
  const b_7315092382398562_noShow = computed(() => false);
  const b_7315092382398562_modalBindings = computed(() => ({
    size: 'lg',
  }));

  const $data = computed(() => ({
    formName: formName.value,
    labelAlign: labelAlign.value,
    item: item.value,
    ux: ux.value,
    b_7315092382398562_noShow: b_7315092382398562_noShow.value,
    b_7315092382398562_modalBindings: b_7315092382398562_modalBindings.value,
  }));

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'formName' },
      { name: 'labelAlign' },
      { name: 'item', cache: { type: 'local', key: 'CreateBillingPlan.item' } },
      { name: 'ux' },
      { name: 'b_7315092382398562_noShow', nodeId: '7315092382398562' },
      { name: 'b_7315092382398562_modalBindings', nodeId: '7315092382398562' },
    ],
    loaders: {},
    ux: {},
    dataRefs: {
      formName,
      labelAlign,
      item,
      ux,
      b_7315092382398562_noShow,
      b_7315092382398562_modalBindings,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  const pageDocument = computed(() => {
    return {
      title: $fn.value.pgettext(
        'CreateBillingPlan|Title',
        'Create Billing Plan'
      ),
    };
  });
  const documentTitle = computed(() => {
    return pageDocument.value
      ? Array.isArray(pageDocument.value.title)
        ? pageDocument.value.title.filter(Boolean).join(' ')
        : pageDocument.value.title
      : null;
  });

  watch(
    () => props.modal,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modal');
    }
  );
  watch(
    () => props.modalName,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('modalName');
    }
  );

  watch(
    pageDocument,
    (newDoc) => {
      if (newDoc) {
        _pageDocument.value = newDoc;
      }
    },
    { immediate: true }
  );

  const act_7315092382398562_closeModal_1bv8lvuk6_getActionArgs = async (
    $event
  ) => {
    return {
      name: props.modalName,
      result: {
        close: true,
        cancel: true,
      },
    };
  };
  const act_2140198529698299_closeComponent_0sg1b6aiu_getActionArgs = async (
    $event
  ) => {
    return {
      result: {
        ok: true,
        value: $event.data,
      },
    };
  };

  const act_7315092382398562_closeModal_1bv8lvuk6 =
    new actionTypes.CloseModalAction({
      actionArgs: act_7315092382398562_closeModal_1bv8lvuk6_getActionArgs,
    });
  const inst_evh_7315092382398562_close_4kro5ge6p =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_closeModal_1bv8lvuk6,
      event: 'close',
      displayName: 'closeModal',
    });
  const inst_evh_7315092382398562_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_close_4kro5ge6p],
    event: 'close',
  });
  const act_7315092382398562_reloadComponentData_04tw1zr8d =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_xv8mrm5kw =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_04tw1zr8d,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_xv8mrm5kw],
    event: 'reload',
  });
  const act_8564662037462133_closeComponent_txw91ecpt =
    new actionTypes.CloseComponentAction({});
  const inst_evh_8564662037462133_close_k24wgrni1 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_closeComponent_txw91ecpt,
      event: 'close',
      displayName: 'closeComponent',
    });
  const inst_evh_8564662037462133_close = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_close_k24wgrni1],
    event: 'close',
  });
  const act_2140198529698299_closeComponent_0sg1b6aiu =
    new actionTypes.CloseComponentAction({
      actionArgs: act_2140198529698299_closeComponent_0sg1b6aiu_getActionArgs,
    });
  const inst_evh_2140198529698299_completed_gq5vy8ou4 =
    new actionTypes.EventHandlerImpl({
      action: act_2140198529698299_closeComponent_0sg1b6aiu,
      event: 'completed',
      displayName: 'closeComponent',
    });
  const inst_evh_2140198529698299_completed = new actionTypes.EventHandlerGroup(
    {
      handlers: [inst_evh_2140198529698299_completed_gq5vy8ou4],
      event: 'completed',
    }
  );
  const act_2434514588044777_closeComponent_a9fjlgmkg =
    new actionTypes.CloseComponentAction({});
  const inst_evh_2434514588044777_clickPreventStop_mvx7pqi6j =
    new actionTypes.EventHandlerImpl({
      action: act_2434514588044777_closeComponent_a9fjlgmkg,
      event: 'click.prevent.stop',
      displayName: 'closeComponent',
    });
  const inst_evh_2434514588044777_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_2434514588044777_clickPreventStop_mvx7pqi6j],
      event: 'click.prevent.stop',
    });

  const actions = {
    inst_evh_7315092382398562_close_4kro5ge6p,
    inst_evh_7315092382398562_close,
    inst_evh_7315092382398562_reload_xv8mrm5kw,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_close_k24wgrni1,
    inst_evh_8564662037462133_close,
    inst_evh_2140198529698299_completed_gq5vy8ou4,
    inst_evh_2140198529698299_completed,
    inst_evh_2434514588044777_clickPreventStop_mvx7pqi6j,
    inst_evh_2434514588044777_clickPreventStop,
  };

  const evh_7315092382398562_close_4kro5ge6p = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close_4kro5ge6p.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_close = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload_xv8mrm5kw = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_xv8mrm5kw.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close_k24wgrni1 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close_k24wgrni1.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_close = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_close.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_completed_gq5vy8ou4 = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_completed_gq5vy8ou4.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2140198529698299_completed = async (event, scope) => {
    return await actions.inst_evh_2140198529698299_completed.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop_mvx7pqi6j = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop_mvx7pqi6j.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_2434514588044777_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_2434514588044777_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    documentTitle,
    pageDocument: _pageDocument,
    routeTab,
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpPage
    :header="documentTitle"
    :loaded="loaded"
    :noShow="b_7315092382398562_noShow"
    :loading="showLoadingIndicator"
    :reloading="reloading"
    :error="error"
    :modal="modal"
    :modalBindings="b_7315092382398562_modalBindings"
    itp-cname="CreateBillingPlan"
    @close="evh_7315092382398562_close($event, {})"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <template #header>
      <UcHeader
        :card="!modal"
        :modal="modal"
        :resourceTitle="
          pgettext('CreateBillingPlan|ResourceTitle', 'Billing Plan')
        "
        :resourceKey="
          pgettext('CreateBillingPlan|ResourceKey', 'Collect items')
        "
        v-if="loaded"
        @close="evh_8564662037462133_close($event, {})"
      >
      </UcHeader>
    </template>
    <UcBody :card="!modal" :modal="modal" v-if="loaded">
      <ItpForm :name="formName">
        <ItpFormGroup
          name="day"
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          required
          :label="pgettext('CreateBillingPlan', 'Target date')"
          :helpTooltip="
            pgettext(
              'CreateBillingPlan',
              'The date from which the system looks for billable items.'
            )
          "
        >
          <ItpFormDatetime name="day" v-model="item.day" required>
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="max_date"
          :label-cols-md="3"
          :content-cols-md="4"
          :labelAlign="labelAlign"
          :label="pgettext('CreateBillingPlan', 'Max date')"
          :helpTooltip="
            pgettext(
              'CreateBillingPlan',
              'If no closing date is provided, items due within ten days of the specified date will be collected.'
            )
          "
        >
          <ItpFormDatetime name="max_date" v-model="item.max_date">
          </ItpFormDatetime>
        </ItpFormGroup>
        <ItpFormGroup
          name="contracts"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          :label="pgettext('CreateBillingPlan', 'Contracts')"
          :helpTooltip="
            pgettext(
              'CreateBillingPlan',
              'Search for the selected contract\'s billable services.'
            )
          "
        >
          <UcContractSelector
            name="contracts"
            :item="item"
            path="contracts"
            multiple
            clearable
          >
          </UcContractSelector>
        </ItpFormGroup>
        <ItpFormGroup
          name="companies"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          :label="pgettext('CreateBillingPlan', 'Companies')"
          :helpTooltip="
            pgettext(
              'CreateBillingPlan',
              'Search for the selected companies\' services.'
            )
          "
        >
          <UcCompanySelector
            name="companies"
            :item="item"
            path="companies"
            multiple
            clearable
          >
          </UcCompanySelector>
        </ItpFormGroup>
        <ItpFormGroup
          name="tenants"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          :label="pgettext('CreateBillingPlan', 'Partners')"
          :helpTooltip="
            pgettext(
              'CreateBillingPlan',
              'Search for the selected partners\' services.'
            )
          "
        >
          <UcCompanySelector
            name="tenants"
            :item="item"
            path="tenants"
            multiple
            clearable
          >
          </UcCompanySelector>
        </ItpFormGroup>
        <ItpFormGroup
          name="rentals"
          :label-cols-md="3"
          :content-cols-md="9"
          :labelAlign="labelAlign"
          :label="pgettext('CreateBillingPlan', 'Rentals')"
          :helpTooltip="
            pgettext(
              'CreateBillingPlan',
              'Search for the selected rentals\' services.'
            )
          "
        >
          <UcCompanySelector
            name="rentals"
            :item="item"
            path="rentals"
            multiple
            clearable
          >
          </UcCompanySelector>
        </ItpFormGroup>
      </ItpForm>
    </UcBody>
    <template #footer>
      <UcFooter :card="!modal" :modal="modal" v-if="loaded">
        <AsyncCommand
          type="submit"
          size="sm"
          operation="create_billing_plan"
          :payload="() => getPayload(item)"
          variant="primary"
          :icon="$config.processIcon"
          :text="pgettext('BillingList|Submit', 'Create billing plan')"
          :form="forms[formName] && forms[formName].id"
          class="ms-2"
          @completed="evh_2140198529698299_completed($event, {})"
        >
        </AsyncCommand>
        <ItpButton
          :text="pgettext('CreateBillingPlan', 'Close')"
          variant="default"
          @click.prevent.stop="
            evh_2434514588044777_clickPreventStop($event, {})
          "
        >
        </ItpButton>
      </UcFooter>
    </template>
  </ItpPage>
</template>
